import { Icon } from '@livable/saas-react-components';
import { FormControl, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import React from 'react';
import RangeDatePicker from 'src/components/general/RangeDatePicker';
import { formatExtractedMoney } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import useStyles from './styles';
const CAD_HINT = 'The common area deduction comes from the property settings.';

/**
 * The most good will that can still be deducted, given what the common area already takes.
 *
 * The two come off the same bill, so they are bounded together rather than each at 100: with a CAD
 * of 30, a GWD of 90 deducts 120% of the bill and the allocation recalculates around a figure that
 * cannot exist (LB-5863). Never negative — a CAD of its own above 100 leaves no room at all rather
 * than room to add some back.
 */
export function maxGwdFor(cadPercent) {
  const cad = Number(cadPercent);
  return Math.max(0, 100 - (Number.isFinite(cad) ? cad : 0));
}

/**
 * Whether a typed good-will deduction is one to pass on.
 *
 * Refused rather than clamped. Clamping as somebody types turns "50" into the maximum the instant
 * the "5" lands and then swallows the "0", which reads as the field fighting them; refusing the
 * keystroke leaves exactly what they had. An empty field is allowed through so it can be cleared and
 * retyped — it is not a value, and the form reads it back as the extracted one.
 *
 * `min` and `max` on the input are not this. They grey out the spinner arrows and set :invalid, and
 * do nothing whatever about a typed or pasted number, which is how both halves of this bug got in.
 */
export function isAcceptableGwd(raw, cadPercent) {
  if (raw === '' || raw === null || raw === undefined) return true;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= maxGwdFor(cadPercent);
}
function ExtractedHint(_ref) {
  let value = _ref.value;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("span", {
    className: classes.extractedHint
  }, "Extracted:", /*#__PURE__*/React.createElement("span", {
    className: classes.extractedHintChip
  }, value));
}

/**
 * The inputs behind a suggested allocation (Figma: allocation table, edit state). Changes are reported
 * as they happen, so the service is asked again and the deductions, recovery and per-unit charges
 * follow along. The common area deduction is a property setting and is shown as read-only.
 */
export function AllocationSuggestionEditForm(_ref2) {
  var _table$cadPercent, _setting$gwdPercent, _ref3, _setting$billAmount, _setting$gwdPercent2;
  let currency = _ref2.currency,
    onChange = _ref2.onChange,
    setting = _ref2.setting,
    table = _ref2.table;
  const classes = useStyles();
  const cadPercent = (_table$cadPercent = table.cadPercent) !== null && _table$cadPercent !== void 0 ? _table$cadPercent : 0;
  const maxGwd = maxGwdFor(cadPercent);
  /**
   * While the suggestion is still being worked out there is no CAD yet, and the table reports it
   * as 0 rather than as unknown -- so the bound would read as the full 100 and let through exactly
   * the figure this refuses a moment later. Nothing to edit against until it lands.
   */
  /**
   * A common area deduction nobody has read yet is not a common area deduction of zero.
   *
   * The table reports an unread one as 0, so the bound would read as the full 100 and let through
   * exactly the figure the service refuses. That is true while the suggestion is in flight AND after
   * one has failed -- a failed lookup clears isLoading and leaves nothing mapped, which is the same
   * absence wearing a different flag.
   */
  const isCadKnown = !table.isLoading && !table.hasFailed;

  /**
   * A saved deduction can stop fitting without anybody touching it.
   *
   * The override is kept per property and utility, and a property's common area deduction can rise
   * afterwards — so a figure that fitted when it was chosen may not fit now, and it is sent on load
   * with no edit involved. Refusing keystrokes does nothing about a value that is already there, so
   * it is called out instead: the service skips that utility, and this says which one and why.
   */
  const currentGwd = Number((_setting$gwdPercent = setting.gwdPercent) !== null && _setting$gwdPercent !== void 0 ? _setting$gwdPercent : table.gwdPercent);
  const isCurrentGwdStale = isCadKnown && Number.isFinite(currentGwd) && !isAcceptableGwd(currentGwd, cadPercent);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.editForm
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editRow
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editField
  }, /*#__PURE__*/React.createElement(RangeDatePicker, {
    format: "MM/dd/yy",
    fromDate: table.serviceStartDate || '',
    handleDateChange: dates => onChange({
      serviceEndDate: dates.endDate,
      serviceStartDate: dates.startDate
    }),
    label: "Service dates",
    noValidate: true,
    toDate: table.serviceEndDate || ''
  }), /*#__PURE__*/React.createElement(ExtractedHint, {
    value: table.extractedPeriod
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.editField
  }, /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: true,
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: "bill-amount-".concat(table.key)
  }, "Bill amount"), /*#__PURE__*/React.createElement(OutlinedInput, {
    id: "bill-amount-".concat(table.key),
    label: "Bill amount",
    onChange: event => onChange({
      billAmount: event.target.value
    }),
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, "$"),
    value: (_ref3 = (_setting$billAmount = setting.billAmount) !== null && _setting$billAmount !== void 0 ? _setting$billAmount : table.extractedAmount) !== null && _ref3 !== void 0 ? _ref3 : ''
  })), /*#__PURE__*/React.createElement(ExtractedHint, {
    value: formatExtractedMoney(table.extractedAmount, currency)
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.deductions
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Deductions"), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.deductionLabel
  }, "Common Area Deduction (CAD): ", /*#__PURE__*/React.createElement("span", {
    className: classes.deductionValue
  }, table.cadPercent, "%")), /*#__PURE__*/React.createElement("span", {
    className: classes.deductionLocked,
    title: CAD_HINT
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "lock-locked"
  }), "Locked")), /*#__PURE__*/React.createElement("div", {
    className: classes.deductionRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.deductionLabel
  }, "Good Will Deduction (GWD):"), /*#__PURE__*/React.createElement(OutlinedInput, {
    className: classes.deductionInput
    // Nothing is left to give away once the common area has taken the whole bill, and
    // nothing to measure against until the common area deduction is known at all.
    ,
    disabled: maxGwd === 0 || !isCadKnown,
    error: isCurrentGwdStale,
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end"
    }, "%"),
    inputProps: {
      'aria-label': 'Good will deduction percent',
      max: maxGwd,
      min: 0,
      type: 'number'
    },
    onChange: event => {
      if (isAcceptableGwd(event.target.value, cadPercent)) {
        onChange({
          gwdPercent: event.target.value
        });
      }
    },
    title: maxGwd === 0 ? 'The common area deduction already accounts for the whole bill.' : isCurrentGwdStale ? "This no longer fits: the common area deduction is now ".concat(cadPercent, "%, so at most ") + "".concat(maxGwd, "% is left to give away.") : "0 to ".concat(maxGwd, "% \u2014 the common area deduction takes the rest."),
    value: (_setting$gwdPercent2 = setting.gwdPercent) !== null && _setting$gwdPercent2 !== void 0 ? _setting$gwdPercent2 : table.gwdPercent
  }))));
}